/**
 * ForeignDebtPage — CRUD de deudas con el exterior (DJ 1930).
 */
import { useState } from 'react'
import { Plus, Pencil, Trash2, AlertCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import {
  useForeignDebts, useCreateForeignDebt, useUpdateForeignDebt, useDeleteForeignDebt,
  type ForeignDebt, type ForeignDebtPayload, type InstrumentType, type GuaranteeType,
} from '../hooks/useForeignDebt'
import { Modal } from '@/components/ds/Modal'

const INSTRUMENTS: { value: InstrumentType; label: string }[] = [
  { value: 'credito', label: 'Crédito bancario' },
  { value: 'bono', label: 'Bono o título de deuda' },
  { value: 'mutuo', label: 'Mutuo' },
  { value: 'linea', label: 'Línea de crédito' },
  { value: 'arriendo', label: 'Arrendamiento financiero (leasing)' },
  { value: 'factoring', label: 'Factoring internacional' },
  { value: 'cta_cte', label: 'Cuenta corriente con relacionada' },
  { value: 'otro', label: 'Otro' },
]

const GUARANTEES: { value: GuaranteeType; label: string }[] = [
  { value: 'sin', label: 'Sin garantía' },
  { value: 'real', label: 'Garantía real' },
  { value: 'personal', label: 'Garantía personal' },
  { value: 'estatal', label: 'Garantía estatal' },
  { value: 'otra', label: 'Otra' },
]

function fmtMoney(value: string, currency = 'USD') {
  const n = parseFloat(value)
  if (isNaN(n)) return '—'
  return n.toLocaleString('es-CL', { style: 'currency', currency, maximumFractionDigits: 0 })
}

function emptyForm(): ForeignDebtPayload {
  return {
    creditor_name: '', creditor_tax_id: '', creditor_country: '',
    is_related_party: false,
    instrument_type: 'credito', guarantee_type: 'sin',
    currency: 'USD',
    principal_amount: '0', outstanding_balance: '0',
    interest_rate: '0',
    disbursement_date: '', maturity_date: null,
    is_active: true, notes: '',
  }
}

export function ForeignDebtPage() {
  const { data, isLoading } = useForeignDebts()
  const create = useCreateForeignDebt()
  const remove = useDeleteForeignDebt()
  const [editing, setEditing] = useState<ForeignDebt | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<ForeignDebtPayload>(emptyForm())
  const [error, setError] = useState<string | null>(null)

  const items = data?.results || []
  const updateMut = useUpdateForeignDebt(editing?.id || '')

  const openCreate = () => {
    setEditing(null); setForm(emptyForm()); setShowForm(true); setError(null)
  }
  const openEdit = (d: ForeignDebt) => {
    setEditing(d)
    setForm({
      creditor_name: d.creditor_name, creditor_tax_id: d.creditor_tax_id,
      creditor_country: d.creditor_country, is_related_party: d.is_related_party,
      instrument_type: d.instrument_type, guarantee_type: d.guarantee_type,
      currency: d.currency,
      principal_amount: d.principal_amount, outstanding_balance: d.outstanding_balance,
      interest_rate: d.interest_rate,
      disbursement_date: d.disbursement_date,
      maturity_date: d.maturity_date,
      is_active: d.is_active, notes: d.notes,
    })
    setShowForm(true); setError(null)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      if (editing) await updateMut.mutateAsync(form)
      else await create.mutateAsync(form)
      setShowForm(false)
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Error al guardar')
    }
  }

  const onDelete = async (d: ForeignDebt) => {
    if (!confirm(`¿Eliminar la deuda con ${d.creditor_name}?`)) return
    try { await remove.mutateAsync(d.id) }
    catch (err: any) { alert(err?.response?.data?.error?.message || 'No se pudo eliminar') }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      {/* Título/subtítulo los provee TreasuryPage (PageHeader). */}
      <div className="flex items-center justify-end">
        <button onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
          <Plus size={14} /> Nueva
        </button>
      </div>

      {isLoading ? (
        <div className="text-content-muted">Cargando...</div>
      ) : items.length === 0 ? (
        <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-8 text-center text-content-muted shadow-card">
          Aún no hay deudas registradas.
        </div>
      ) : (
        <DataTable
          columns={[
            {
              key: 'creditor_name', header: 'Acreedor',
              render: d => (
                <>
                  {d.creditor_name}
                  {d.is_related_party && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-warning/20 text-warning">relacionada</span>}
                  {d.creditor_tax_id && <div className="text-xs text-content-muted font-mono">{d.creditor_tax_id}</div>}
                </>
              ),
            },
            { key: 'creditor_country', header: 'País', render: d => <span className="font-mono text-content-subtle">{d.creditor_country}</span> },
            { key: 'instrument_type_display', header: 'Instrumento', render: d => <span className="text-content-subtle">{d.instrument_type_display}</span> },
            { key: 'currency', header: 'Moneda', render: d => <span className="text-content-subtle">{d.currency}</span> },
            {
              key: 'principal_amount', header: 'Capital', numeric: true,
              sortValue: d => Number(d.principal_amount),
              render: d => fmtMoney(d.principal_amount, d.currency),
            },
            {
              key: 'outstanding_balance', header: 'Saldo', numeric: true,
              sortValue: d => Number(d.outstanding_balance),
              render: d => fmtMoney(d.outstanding_balance, d.currency),
            },
            { key: 'interest_rate', header: 'Tasa', numeric: true, render: d => `${d.interest_rate}%` },
            { key: 'disbursement_date', header: 'Desembolso', render: d => <span className="text-content-subtle">{d.disbursement_date}</span> },
            {
              key: 'acciones', header: '',
              render: d => (
                <div className="text-right">
                  <button onClick={() => openEdit(d)} className="text-primary hover:text-primary-hover mr-2"><Pencil size={14} /></button>
                  <button onClick={() => onDelete(d)} className="text-danger hover:text-danger-hover"><Trash2 size={14} /></button>
                </div>
              ),
            },
          ]}
          rows={items}
          rowKey={d => d.id}
          minWidth={1000}
          caption="Deuda con el exterior"
        />
      )}

      {showForm && (
        <Modal
          open
          onClose={() => setShowForm(false)}
          size="lg"
          title={editing ? 'Editar Deuda' : 'Nueva Deuda con el Exterior'}
          actions={
            <>
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-surface-hover">Cancelar</button>
              <button type="submit" form="deuda-exterior-form"
                className="px-4 py-2 text-sm font-semibold bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
                {editing ? 'Guardar' : 'Crear'}
              </button>
            </>
          }
        >
          {/* El pie lo pinta el Modal fuera de este <form>: el botón de guardar lo alcanza por `form=`. */}
          <form id="deuda-exterior-form" onSubmit={onSubmit}>
          <div className="space-y-3">
            {error && (
              <div className="rounded border border-danger bg-danger/10 p-3 text-sm text-danger flex gap-2">
                <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Acreedor *">
                <input required value={form.creditor_name}
                  onChange={(e) => setForm({ ...form, creditor_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content" />
              </Field>
              <Field label="Tax ID acreedor">
                <input value={form.creditor_tax_id}
                  onChange={(e) => setForm({ ...form, creditor_tax_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content font-mono" />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="País (ISO-2) *">
                <input required maxLength={2} value={form.creditor_country}
                  onChange={(e) => setForm({ ...form, creditor_country: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content font-mono uppercase" />
              </Field>
              <Field label="Moneda">
                <input value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content font-mono uppercase" />
              </Field>
              <Field label="¿Parte relacionada?">
                <label className="flex items-center gap-2 mt-2 text-sm">
                  <input type="checkbox" checked={form.is_related_party}
                    onChange={(e) => setForm({ ...form, is_related_party: e.target.checked })} />
                  Sí, es relacionada
                </label>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Instrumento">
                <select value={form.instrument_type}
                  onChange={(e) => setForm({ ...form, instrument_type: e.target.value as InstrumentType })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content">
                  {INSTRUMENTS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </Field>
              <Field label="Garantía">
                <select value={form.guarantee_type}
                  onChange={(e) => setForm({ ...form, guarantee_type: e.target.value as GuaranteeType })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content">
                  {GUARANTEES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Monto original">
                <input type="number" step="0.01" required value={form.principal_amount}
                  onChange={(e) => setForm({ ...form, principal_amount: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content tabular-nums" />
              </Field>
              <Field label="Saldo vigente">
                <input type="number" step="0.01" value={form.outstanding_balance}
                  onChange={(e) => setForm({ ...form, outstanding_balance: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content tabular-nums" />
              </Field>
              <Field label="Tasa anual (%)">
                <input type="number" step="0.001" value={form.interest_rate}
                  onChange={(e) => setForm({ ...form, interest_rate: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content tabular-nums" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha desembolso *">
                <input type="date" required value={form.disbursement_date}
                  onChange={(e) => setForm({ ...form, disbursement_date: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content" />
              </Field>
              <Field label="Fecha vencimiento">
                <input type="date" value={form.maturity_date || ''}
                  onChange={(e) => setForm({ ...form, maturity_date: e.target.value || null })}
                  className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content" />
              </Field>
            </div>
            <Field label="Notas">
              <textarea rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-divider rounded bg-canvas text-content" />
            </Field>
          </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-content-subtle mb-1">{label}</span>
      {children}
    </label>
  )
}

export default ForeignDebtPage
